import { useState, useEffect } from 'react';
import { X, Cookie, Shield, Eye, ExternalLink } from 'lucide-react';
import { Link } from 'react-router-dom';

const CookieConsent = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);

  useEffect(() => {
    // Check if user has already consented
    const hasConsented = localStorage.getItem('cookieConsent');
    if (!hasConsented) {
      // Show banner after a short delay for better UX
      const timer = setTimeout(() => {
        setIsVisible(true);
      }, 1000);
      return () => clearTimeout(timer);
    } else {
      // Apply stored consent to Google Consent Mode on load
      const gtag = (window as any).gtag as undefined | ((...args: unknown[]) => void);
      if (gtag) {
        if (hasConsented === 'all') {
          gtag('consent', 'update', {
            ad_storage: 'granted',
            ad_user_data: 'granted',
            ad_personalization: 'granted',
            analytics_storage: 'granted',
          });
        } else if (hasConsented === 'essential') {
          gtag('consent', 'update', {
            ad_storage: 'denied',
            ad_user_data: 'denied',
            ad_personalization: 'denied',
            analytics_storage: 'granted',
          });
        } else if (hasConsented === 'rejected') {
          gtag('consent', 'update', {
            ad_storage: 'denied',
            ad_user_data: 'denied',
            ad_personalization: 'denied',
            analytics_storage: 'denied',
          });
        }
      }
    }
  }, []);

  const handleAcceptAll = () => {
    localStorage.setItem('cookieConsent', 'all');
    localStorage.setItem('cookieConsentDate', new Date().toISOString());
    const gtag = (window as any).gtag as undefined | ((...args: unknown[]) => void);
    if (gtag) {
      gtag('consent', 'update', {
        ad_storage: 'granted',
        ad_user_data: 'granted',
        ad_personalization: 'granted',
        analytics_storage: 'granted',
      });
    }
    setIsVisible(false);
  };

  const handleAcceptEssential = () => {
    localStorage.setItem('cookieConsent', 'essential');
    localStorage.setItem('cookieConsentDate', new Date().toISOString());
    const gtag = (window as any).gtag as undefined | ((...args: unknown[]) => void);
    if (gtag) {
      gtag('consent', 'update', {
        ad_storage: 'denied',
        ad_user_data: 'denied',
        ad_personalization: 'denied',
        analytics_storage: 'granted',
      });
    }
    setIsVisible(false);
  };

  const handleReject = () => {
    localStorage.setItem('cookieConsent', 'rejected');
    localStorage.setItem('cookieConsentDate', new Date().toISOString());
    const gtag = (window as any).gtag as undefined | ((...args: unknown[]) => void);
    if (gtag) {
      gtag('consent', 'update', {
        ad_storage: 'denied',
        ad_user_data: 'denied',
        ad_personalization: 'denied',
        analytics_storage: 'denied',
      });
    }
    setIsVisible(false);
  };

  if (!isVisible) return null;

  return (
    <>
      {/* Overlay */}
      <div className="fixed inset-0 bg-black bg-opacity-30 z-40 backdrop-blur-sm" />
      
      {/* Cookie Consent Banner */}
      <div className="fixed bottom-4 left-0 right-0 z-50 animate-slide-up">
        <div className="mx-auto px-3 py-3 max-w-sm sm:max-w-md md:max-w-lg">
          <div className="bg-white rounded-lg shadow-2xl border-2 border-primary-500 overflow-hidden max-h-[70vh]">
            {/* Header */}
            <div className="bg-gradient-to-r from-primary-600 to-primary-700 px-4 py-3 flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <Cookie className="text-white" size={22} />
                <h3 className="text-lg font-bold text-white">Cookie Consent & Privacy Notice</h3>
              </div>
              <button
                onClick={handleReject}
                className="text-white hover:text-gray-200 transition-colors"
                aria-label="Close"
              >
                <X size={20} />
              </button>
            </div>

            {/* Content */}
            <div className="p-4 overflow-y-auto">
              <p className="text-gray-700 text-sm mb-3 leading-relaxed">
                We use cookies and similar technologies to enhance your browsing experience, analyze site traffic, 
                and serve personalized advertisements through <strong>Google AdSense</strong>. By clicking "Accept All Cookies," 
                you consent to the use of all cookies as described in our{' '}
                <Link to="/privacy" className="text-primary-600 hover:text-primary-700 underline font-semibold">
                  Privacy Policy
                </Link>.
              </p>

              {/* Key Points */}
              <div className="hidden md:grid md:grid-cols-3 gap-3 mb-4">
                <div className="flex items-start space-x-3 bg-blue-50 p-3 rounded-lg">
                  <Shield className="text-blue-600 flex-shrink-0 mt-1" size={20} />
                  <div>
                    <h4 className="font-semibold text-gray-900 text-sm mb-1">Your Privacy</h4>
                    <p className="text-xs text-gray-600">We respect your privacy and protect your data</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3 bg-green-50 p-3 rounded-lg">
                  <Cookie className="text-green-600 flex-shrink-0 mt-1" size={20} />
                  <div>
                    <h4 className="font-semibold text-gray-900 text-sm mb-1">Essential Cookies</h4>
                    <p className="text-xs text-gray-600">Required for website functionality</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3 bg-purple-50 p-3 rounded-lg">
                  <Eye className="text-purple-600 flex-shrink-0 mt-1" size={20} />
                  <div>
                    <h4 className="font-semibold text-gray-900 text-sm mb-1">Ad Personalization</h4>
                    <p className="text-xs text-gray-600">Google AdSense for relevant ads</p>
                  </div>
                </div>
              </div>

              {/* Toggle Details */}
              <button
                onClick={() => setShowDetails(!showDetails)}
                className="text-primary-600 hover:text-primary-700 text-sm font-semibold mb-4 flex items-center"
              >
                {showDetails ? '− Hide' : '+ Show'} Cookie Details
              </button>

              {/* Details Section */}
              {showDetails && (
                <div className="bg-gray-50 rounded-lg p-4 mb-6 space-y-3 text-sm">
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">🔹 Essential Cookies (Required)</h4>
                    <p className="text-gray-600 text-xs">
                      These cookies are necessary for the website to function and cannot be disabled. 
                      They include session cookies, authentication, and security features.
                    </p>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">🔹 Performance & Analytics Cookies</h4>
                    <p className="text-gray-600 text-xs">
                      Help us understand how visitors interact with our website by collecting anonymous information. 
                      This helps us improve the site performance and user experience.
                    </p>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">🔹 Advertising Cookies (Google AdSense)</h4>
                    <p className="text-gray-600 text-xs">
                      Used by Google AdSense to deliver personalized advertisements based on your interests. 
                      These cookies track your browsing behavior across websites. You can opt out at{' '}
                      <a 
                        href="https://www.google.com/settings/ads" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-primary-600 hover:text-primary-700 underline inline-flex items-center"
                      >
                        Google Ads Settings <ExternalLink size={12} className="ml-1" />
                      </a>.
                    </p>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">🔹 Functional Cookies</h4>
                    <p className="text-gray-600 text-xs">
                      Remember your preferences and settings to provide enhanced features and personalization.
                    </p>
                  </div>
                  <div className="bg-yellow-50 border-l-4 border-yellow-400 p-3 mt-3">
                    <p className="text-xs text-gray-700">
                      <strong>Note:</strong> All carrier data on our platform is from publicly available government databases 
                      (USDOT, FMCSA). For data removal requests, visit our{' '}
                      <Link to="/data-removal" className="text-primary-600 hover:text-primary-700 underline font-semibold">
                        Data Removal page
                      </Link>.
                    </p>
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  onClick={handleAcceptAll}
                  className="flex-1 px-5 py-2.5 bg-primary-600 text-black rounded-lg font-semibold hover:bg-primary-700 transition-colors shadow-lg text-sm active:text-black focus:text-black"
                >
                  ✓ Accept All Cookies
                </button>
                <button
                  onClick={handleAcceptEssential}
                  className="flex-1 px-5 py-2.5 bg-gray-600 text-white rounded-lg font-semibold hover:bg-gray-700 transition-colors text-sm"
                >
                  Accept Essential Only
                </button>
                <button
                  onClick={handleReject}
                  className="flex-1 px-5 py-2.5 bg-white border-2 border-gray-300 text-gray-700 rounded-lg font-semibold hover:bg-gray-50 transition-colors text-sm"
                >
                  Reject All
                </button>
              </div>

              {/* Links */}
              <div className="mt-4 text-center text-xs text-gray-500">
                <Link to="/privacy" className="hover:text-primary-600 underline">Privacy Policy</Link>
                {' • '}
                <Link to="/terms" className="hover:text-primary-600 underline">Terms of Use</Link>
                {' • '}
                <Link to="/data-removal" className="hover:text-primary-600 underline">Data Removal Request</Link>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes slide-up {
          from {
            transform: translateY(100%);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }
        .animate-slide-up {
          animation: slide-up 0.4s ease-out;
        }
      `}</style>
    </>
  );
};

export default CookieConsent;

